import { Injectable, OnDestroy, inject } from '@angular/core';
import { map, takeUntil } from 'rxjs/operators';
import { Subject, firstValueFrom } from 'rxjs';
import { QuotationsService } from 'src/app/@pages/quotations/quotations.service';
import { INotasGrap } from 'src/app/@interfaces/notas.interface';
import { TranslateService } from '@ngx-translate/core';
import { CustomerService } from '../../../../../@pages/customer/customer.service';
import { PortfolioService } from 'src/app/@pages/portfolio/portfolio.service';
import {
  IInvoicePayments,
  IPaymentsList,
  IPurchasePayments,
} from 'src/app/@interfaces/payments.interface';
import { UntypedFormGroup } from '@angular/forms';
import { TreasuryService } from '../../../../../@pages/treasury/treasury.service';
import { ITreasury } from 'src/app/@interfaces/treasury.interface';
import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';
import { AlertsService } from '../../../services/alerts/alerts.service';
import { IQuotation } from 'src/app/@interfaces/quotation.interface';
import { AlertService } from '@shared-services';

@Injectable({
  providedIn: 'root',
})
export class PaymentsService implements OnDestroy {
  unsubscribe$ = new Subject<void>();
  private alertService = inject(AlertService);

  constructor(
    private quotationService: QuotationsService,
    private translate: TranslateService,
    private customerService: CustomerService,
    private portfolioService: PortfolioService,
    private treasuryService: TreasuryService,
    private alertsService: AlertsService,
    private cookieAuthService: CookieAuthService,
  ) {}

  getInvoicePayments(invoice_id: number): Promise<IInvoicePayments[]> {
    return firstValueFrom(this.portfolioService.getPaymenInvoice(invoice_id));
  }

  async getCreditValues(id_factura: number): Promise<number> {
    const credit: any[] = await firstValueFrom(
      this.quotationService
        .getCredito(parseInt(id_factura.toString()))
        .pipe(takeUntil(this.unsubscribe$)),
    );
    return credit.reduce((total, C) => total + C.total * -1, 0);
  }

  async getDebitsValues(id_factura: number): Promise<number> {
    const debit: any[] = await firstValueFrom(
      this.quotationService
        .getDebito(parseInt(id_factura.toString()))
        .pipe(takeUntil(this.unsubscribe$)),
    );
    return debit.reduce((total, D) => total + D.total, 0);
  }

  getInvoice(id_factura: number): Promise<any> {
    return firstValueFrom(
      this.quotationService
        .getQuotation(id_factura)
        .pipe(takeUntil(this.unsubscribe$)),
    );
  }

  getAdvancePayments(
    id_customer: number | null,
    id_provider: number | null,
    type: string,
  ): Promise<ITreasury[]> {
    return firstValueFrom(
      this.treasuryService
        .getAdvancePaymentsByType(id_customer, id_provider, type)
        .pipe(takeUntil(this.unsubscribe$)),
    );
  }

  async getPositiveBalance(id_customer: number): Promise<number> {
    const result = await firstValueFrom(
      this.customerService
        .getCustomer(id_customer)
        .pipe(takeUntil(this.unsubscribe$)),
    );
    return result.positive_balance;
  }

  getCashierList(): Promise<IPaymentsList[]> {
    const rol = this.cookieAuthService.getRolId!;
    const user = this.cookieAuthService.getUserId!;
    const source$ =
      rol === '1' || rol === '3'
        ? this.portfolioService.getCashierList()
        : this.portfolioService.getCashierListByUser(parseInt(user));
    return firstValueFrom(source$.pipe(takeUntil(this.unsubscribe$)));
  }

  getBankList(): Promise<IPaymentsList[]> {
    const rol = this.cookieAuthService.getRolId!;
    const user = this.cookieAuthService.getUserId!;
    const source$ =
      rol === '1' || rol === '3'
        ? this.portfolioService.getBankList()
        : this.portfolioService.getBankListByUser(parseInt(user));
    return firstValueFrom(source$.pipe(takeUntil(this.unsubscribe$)));
  }

  async ensurePaymentTypeAssigned(
    id_factura: number,
    id_payment: number,
    paymentMethodType: number,
  ): Promise<boolean> {
    const exists = await firstValueFrom(
      this.quotationService
        .validInvoicePaymentExits(id_factura, id_payment)
        .pipe(takeUntil(this.unsubscribe$)),
    );

    if (exists) {
      return true;
    }

    await firstValueFrom(
      this.portfolioService
        .assingInvoicePays(id_factura, id_payment, paymentMethodType)
        .pipe(takeUntil(this.unsubscribe$)),
    );

    return true;
  }

  updatePositiveBalanceByInvoice(id_factura: number, positiveBalance: number) {
    this.quotationService
      .updatePositiveBalanceInvoice(id_factura, positiveBalance)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        error: (error) => {
          console.error('Error updating positive balance by invoice', error);
          this.alertService.open({
            title: this.translate.instant('Error'),
            message: this.translate.instant('Something was wrong'),
            type: 'error',
            acceptText: 'Ok',
          });
        },
      });
  }

  validCompletePayments(
    invoicePayments: IInvoicePayments[] | IPurchasePayments[],
  ) {
    const valid = invoicePayments.map((pay) => {
      return pay.status;
    });
    let total = valid.reduce((a, b) => a + b, 0);
    return total === invoicePayments.length * 3 ? true : false;
  }

  getDescriptionPayment(
    valuePositiveBalance: boolean,
    paymentsForm: UntypedFormGroup,
  ) {
    let descriptionPayment = '';
    if (valuePositiveBalance === true) {
      descriptionPayment = 'positive_balance';
    } else {
      descriptionPayment = paymentsForm.get('types')?.value;
    }
    return descriptionPayment;
  }

  createValidations(
    paymentsForm: UntypedFormGroup,
    value: number,
    positiveBalanceValue: number,
    valuePositiveBalance: boolean,
    totalPaidQuot: number,
    paymentType: IPaymentsList[],
  ): boolean {
    if (paymentsForm.invalid) {
      this.alertsService.showErrorAlert(
        'Please, fill in all the required fields!',
      );
      return false;
    } else if (value <= 0) {
      this.alertsService.showErrorAlert(
        'You cannot make a payment of less than 0!',
      );
      return false;
    } else if (value > positiveBalanceValue && valuePositiveBalance) {
      this.alertsService.showErrorAlert(
        'You cannot make a payment greater than the balance in favor that the client has!',
      );
      return false;
    } else if (totalPaidQuot === 0 && valuePositiveBalance) {
      this.alertsService.showErrorAlert(
        'You cannot make the payment with the balance in favor!',
      );
      return false;
    } else if (paymentType.length <= 0) {
      this.alertsService.showErrorAlert('There is no payment on this invoice!');
      return false;
    } else {
      return true;
    }
  }

  async updatePositiveBalanceByCustomer(
    id_customer: number,
    positiveBalance: number,
    notifySuccess = true,
  ): Promise<boolean> {
    const result = await firstValueFrom(
      this.customerService
        .updatePositiveBalance(id_customer, positiveBalance)
        .pipe(takeUntil(this.unsubscribe$)),
    );

    if (result.nombre === 'customerExist') {
      this.alertService.open({
        title: this.translate.instant('Error'),
        message: this.translate.instant('Something was wrong'),
        type: 'error',
        acceptText: 'Ok',
      });
      return false;
    }

    if (notifySuccess) {
      this.alertService.open({
        title: this.translate.instant('Good'),
        message: this.translate.instant('Customer updated successfully'),
        type: 'success',
        acceptText: 'Ok',
      });
    }
    return true;
  }

  getTotalWithAdvancePayment(totalPaid: number, advancePaymentValue: number) {
    return totalPaid >= advancePaymentValue ? advancePaymentValue : totalPaid;
  }

  advancePaymentValidations(advancePayment: ITreasury, value: number) {
    const avaliable = parseFloat(
      (advancePayment.value - advancePayment.value_used).toFixed(2),
    );
    if (value > avaliable && advancePayment) {
      this.alertsService.showErrorAlert(
        'You cannot make a payment greater than the advance payment that the client has!',
      );
      return false;
    } else if (value <= 0 && advancePayment) {
      this.alertsService.showErrorAlert(
        'You cannot make the payment with the advance payment!',
      );
      return false;
    } else {
      return true;
    }
  }

  relateAdvancePayment(
    action: string,
    type: string,
    id_payment: number,
    id_treasury: number,
    value: number,
  ): Promise<boolean> {
    if (action === 'Set') {
      return type === 'Invoice'
        ? this.setRelateInvoice(id_payment, id_treasury, value)
        : this.setRelatePurchase(id_payment, id_treasury, value);
    }
    return type === 'Invoice'
      ? this.removeRelateInvoice(id_payment, id_treasury, value)
      : this.removeRelatePurchase(id_payment, id_treasury, value);
  }

  setRelateInvoice(
    id_payinvoice: number,
    id_treasury: number,
    value: number,
  ): Promise<boolean> {
    return firstValueFrom(
      this.treasuryService
        .setInvoicePaymentTreasury(id_payinvoice, id_treasury, value)
        .pipe(
          takeUntil(this.unsubscribe$),
          map(() => true),
        ),
    );
  }

  removeRelateInvoice(
    id_payinvoice: number,
    id_treasury: number,
    value: number,
  ): Promise<boolean> {
    return firstValueFrom(
      this.treasuryService
        .deleteInvoicePaymentTreasury(id_payinvoice, id_treasury, value)
        .pipe(
          takeUntil(this.unsubscribe$),
          map(() => true),
        ),
    );
  }

  setRelatePurchase(
    id_paypurchases: number,
    id_treasury: number,
    value: number,
  ): Promise<boolean> {
    return firstValueFrom(
      this.treasuryService
        .setPurchasePaymentTreasury(id_paypurchases, id_treasury, value)
        .pipe(
          takeUntil(this.unsubscribe$),
          map(() => true),
        ),
    );
  }

  removeRelatePurchase(
    id_paypurchases: number,
    id_treasury: number,
    value: number,
  ): Promise<boolean> {
    return firstValueFrom(
      this.treasuryService
        .deletePurchasePaymentTreasury(id_paypurchases, id_treasury, value)
        .pipe(
          takeUntil(this.unsubscribe$),
          map(() => true),
        ),
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
